import React, { useMemo } from 'react'
import { SvgIcon, Typography, Stack } from '@mui/material'
import { MetricsCardProps, LegendItem, CardData } from './types'
import { getActivityStatusDetailsIcon } from '../../hooks/useActivityStatuses/useActivityStatuses.helpers'
import { useCardExpansion } from './hooks/useCardExpansion'
import { CardRow } from './components/CardRow'
import { ExpandButton } from './components/ExpandButton'
import { ScrollWrapper } from '../ScrollWrapper'
import { ReactComponent as ScalesIcon } from '../../../assets/images/Scales.svg'
import { ReactComponent as BarbellIcon } from '../../../assets/images/Barbell.svg'
import { OverflowTooltipWrapper } from '../OverflowTooltipWrapper/OverflowTooltipWrapper'

const ICON_MAP = {
    barbell: BarbellIcon,
    scales: ScalesIcon,
}

const CardHeader = ({ icon, title }: Partial<CardData>) => {
    return (
        <Stack direction="row" alignItems="center" gap={1} color="figma.grayscale.textHier1">
            <SvgIcon
                component={ICON_MAP[icon]}
                inheritViewBox
                fontSize="small"
                color="inherit"
                sx={{ fontSize: '24px' }}
            />
            <OverflowTooltipWrapper
                placement="top-start"
                arrow
                title={
                    <Stack gap={1} overflow="scroll">
                        <Typography
                            variant="14x400"
                            color="figma.grayscale.textHier1"
                            sx={{
                                whiteSpace: 'pre-line',
                                overflow: 'scroll',
                                maxHeight: '50vh',
                            }}
                        >
                            {title}
                        </Typography>
                    </Stack>
                }
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: 'figma.grayscale.tier7',
                            px: 2,
                            py: 1,
                            borderRadius: '6px',
                            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                        },
                    },
                    arrow: {
                        sx: {
                            color: 'figma.grayscale.tier7',
                        },
                    },
                }}
            >
                <Typography variant="20x500" color="inherit" noWrap>
                    {title}
                </Typography>
            </OverflowTooltipWrapper>
        </Stack>
    )
}

const CardSubtitle = ({ subtitle }: Partial<CardData>) => (
    <Typography variant="18x600" color="figma.grayscale.textHier1">
        {subtitle}
    </Typography>
)

const LegendHeader = ({ items, legend }: Partial<CardData>) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="18x600" color="figma.grayscale.textHier1">
            Total: {items.length}
        </Typography>
        <Stack direction="row" gap={1.5}>
            {legend.map((item) => (
                <Stack direction="row" alignItems="center" gap={1} key={item.label}>
                    <SvgIcon
                        component={getActivityStatusDetailsIcon({ shape: item.shape, color: item.color })}
                        inheritViewBox
                        fontSize="small"
                        sx={{
                            fontSize: '12px',
                        }}
                    />
                    <Typography variant="16x400" color="figma.grayscale.tier3">
                        {item.label}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    </Stack>
)

const MAX_INITIAL_ITEMS_GRAPH = 6

const MetricsCard = ({ data, viewMode }: MetricsCardProps) => {
    const { isExpanded, onToggleExpand, onScrollExpand, itemsToShow, totalItems, canExpand, visibleItemsCount } =
        useCardExpansion(data.items, viewMode)
    const isScrollable = viewMode === 'graphs' && itemsToShow.length > MAX_INITIAL_ITEMS_GRAPH

    const maxValue = useMemo(() => {
        if (data.items.length === 0) return 0
        const allValues = data.items.map((item) => item.value)
        return Math.max(...allValues, 0)
    }, [data.items])

    return (
        <Stack direction="column" p={2} gap={2} border={1} borderColor="figma.grayscale.stroke" borderRadius={2}>
            <CardHeader icon={data.icon} title={data.title} />

            <Stack direction="column" gap={1.5}>
                {data.subtitle && viewMode === 'graphs' && <CardSubtitle subtitle={data.subtitle} />}
                {data.legend && <LegendHeader items={data.items} legend={data.legend} />}
                <ScrollWrapper disabled={!isScrollable} onScroll={onScrollExpand} containerSx={{ maxHeight: 380 }}>
                    <Stack direction="column" gap={viewMode === 'list' ? 1.5 : 0}>
                        {itemsToShow.map((item, index) => (
                            <CardRow
                                key={item.id}
                                item={item}
                                viewMode={viewMode}
                                index={index}
                                subtitle={data.subtitle}
                                showValue={!data.legend}
                                maxValue={maxValue}
                            />
                        ))}
                    </Stack>
                </ScrollWrapper>
            </Stack>

            {canExpand && (
                <ExpandButton
                    isExpanded={isExpanded}
                    onToggle={onToggleExpand}
                    totalCount={totalItems}
                    visibleCount={visibleItemsCount}
                />
            )}
        </Stack>
    )
}

export default MetricsCard
